import { i18n } from '@/i18n'
import type { CurrencyCode } from '@/types'
import { getTheme } from '@goodlifeplus/themes'
import type { SiteId, ThemeType } from '@goodlifeplus/themes/types'
import { toValue } from 'vue'
import type { Locale } from 'vue-i18n'

export class SettingsService {
  private static currencyMap: Record<Locale, CurrencyCode> = {
    'en-GB': 'GBP',
    'en-IE': 'EUR',
  }

  private static disabledRoutesMap: Record<Locale | ThemeType, string[]> = {
    'en-GB': [],
    'en-IE': ['benefits', 'daily-prizes', 'as-seen-in', 'membership-prizes'],
    club: ['good-causes'],
  }

  public static getLocale(): Locale {
    return toValue(i18n.global.locale) ?? 'en-GB'
  }

  public static getCurrencyCode(): CurrencyCode {
    const locale = SettingsService.getLocale()
    return SettingsService.currencyMap[locale] ?? 'GBP'
  }

  public static isGbLocale(): boolean {
    return SettingsService.getLocale() === 'en-GB'
  }

  public static isIeLocale(): boolean {
    return SettingsService.getLocale() === 'en-IE'
  }

  public static getDisabledRoutes(): string[] {
    const locale = SettingsService.getLocale()
    const themeType = getTheme(import.meta.env.GLP_SITE_ID as SiteId).type
    return [
      ...(SettingsService.disabledRoutesMap[locale] ?? []),
      ...(SettingsService.disabledRoutesMap[themeType] ?? []),
    ]
  }

  public static isSectionEnabled(sectionName: string): boolean {
    const disabledRoutes = SettingsService.getDisabledRoutes()
    return !disabledRoutes.includes(sectionName)
  }

  /**
   * Fetches the site pixel ID from the CMS settings endpoint.
   *
   * @returns {Promise<string>} The site pixel ID. Default to GLP pixel ID if not found.
   */
  public static async getSitePixelId(): Promise<string | null> {
    // try {
    //   const { data, error } = await fetcher.get<{ pixel_id: string }>(
    //     toCmsUrl(`/site-config`)
    //   )

    //   if (error.value || !data.value || !data.value.pixel_id) {
    //     throw new Error('Failed to fetch site pixel ID')
    //   }

    //   return data.value.pixel_id
    // } catch (error) {
    //   return import.meta.env.GLP_PIXEL_ID ?? null
    // }

    return import.meta.env.GLP_PIXEL_ID ?? null
  }

  public static getSiteTagId(): string | null {
    return import.meta.env.GLP_GA_TAG_ID ?? null
  }

  public static getSiteMeasurementId(): string | null {
    return import.meta.env.GLP_GA_MEASUREMENT_ID ?? null
  }

  // Static properties for one-time use
  public static currencyCode = SettingsService.getCurrencyCode()
  public static disabledRoutes = SettingsService.getDisabledRoutes()
}
